import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { PIVOT_LABELS, type Pivot } from "@/components/layout/pivotRoutes.ts";
import {
  SETTINGS_TAB_ICONS,
  SKILLS_GATE,
  useDescriptiveTabsInSettings,
} from "@/views/Settings/settingsNav.ts";
import { useGateValue } from "@statsig/react-bindings";
import { TASKS_GATE, useTaskGates } from "@/components/userTasks/useTaskGates.ts";
import { CalendarDays, CircleCheck, SquarePen } from "lucide-react";
import type { ComponentType } from "react";

export type NavIcon = ComponentType<{ className?: string }>;

// Re-exported so the rows, their names, and the type naming them stay one
// import for everything that draws this list.
export { PIVOT_LABELS, type Pivot };

/**
 * Every destination the canvas opens, in the order Frank set. Search leads: it
 * reaches all of the others' contents, so it reads as the way in rather than as
 * one more place to go.
 *
 * One list, not two: every row means the same thing — what the canvas beside it
 * shows — and grouping them by which subsystem owns the page behind them only
 * makes the display order harder to read off.
 */
const NAV_ROWS: { value: Pivot; label: string; icon: NavIcon; gate?: string }[] = [
  // Calendar and Tasks are the only two with no settings tab behind them, so
  // they name their own glyph. Every other row draws from SETTINGS_TAB_ICONS, so
  // a destination cannot be one picture here and another in settings.
  { value: "library", label: PIVOT_LABELS.library, icon: SETTINGS_TAB_ICONS.library },
  // Tasks ahead of Calendar: a list of what the user owes is read more often
  // than the day is. Native orders its bar the same way.
  { value: "tasks", label: PIVOT_LABELS.tasks, icon: CircleCheck, gate: TASKS_GATE },
  { value: "calendar", label: PIVOT_LABELS.calendar, icon: CalendarDays },
  // Connectors sits with the rows the user works in rather than with the ones
  // describing what okthx can do: what the assistant is plugged into decides
  // what it can do at all, so it is read far more often than Routines or Skills.
  { value: "connectors", label: PIVOT_LABELS.connectors, icon: SETTINGS_TAB_ICONS.connectors },
];

/**
 * Where Routines and Skills sit when the nav list carries them — after the rows
 * above, so the rows the user works in lead and the ones describing what okthx
 * can do follow. Inside the tasks rollout they are settings rows instead and
 * neither is listed.
 */
const DESCRIPTIVE_NAV_ROWS: { value: Pivot; label: string; icon: NavIcon; gate?: string }[] = [
  { value: "routines", label: PIVOT_LABELS.routines, icon: SETTINGS_TAB_ICONS.routines },
  {
    value: "skills",
    label: PIVOT_LABELS.skills,
    icon: SETTINGS_TAB_ICONS.skills,
    gate: SKILLS_GATE,
  },
];

/**
 * The rows this account can see. The gates are read here rather than at each
 * surface, so the sidebar and the mobile menu cannot answer them differently —
 * which they did: mobile filtered Tasks out and desktop offered it to everyone,
 * including the accounts the gate exists to keep out of it.
 *
 * Every gate is read unconditionally and the answers collected into a map, since
 * a hook cannot be called per row. The same shape `useEnabledSettingsTabs` uses
 * for the settings rail.
 *
 * Tasks is not ready for everyone, and Skills takes the settings tab's own gate,
 * so no two surfaces disagree about who sees either.
 */
export const useNavRows = () => {
  // Either task feature puts Tasks in the nav: the pivot shows the new list for
  // tasks-v2 and the per-thread view for tasks (see TasksPivotView).
  const { any: tasksEnabled } = useTaskGates();
  const skillsEnabled = useGateValue(SKILLS_GATE);
  const descriptiveInSettings = useDescriptiveTabsInSettings();
  const gateEnabled: Record<string, boolean> = {
    [TASKS_GATE]: tasksEnabled,
    [SKILLS_GATE]: skillsEnabled,
  };

  const rows = descriptiveInSettings ? NAV_ROWS : [...NAV_ROWS, ...DESCRIPTIVE_NAV_ROWS];
  return rows.filter((row) => !row.gate || gateEnabled[row.gate]);
};

/**
 * One row of the list. Wears the thread row's own metrics — `h-7`, `rounded-lg`,
 * transparent until hovered, and the `-mx-2` / `px-2` pair that bleeds the hover
 * surface into the gutter — so a nav row and a thread row below it sit on the
 * same left edge and light up the same way.
 */
const NavRow = ({
  label,
  icon: Icon,
  isActive = false,
  onClick,
  statsigName,
}: {
  label: string;
  icon: NavIcon;
  isActive?: boolean;
  onClick: () => void;
  statsigName: string;
}) => (
  <Button
    type="button"
    variant="ghost"
    className={cn(
      "-mx-2 h-7 w-[calc(100%+1rem)] justify-start gap-2 rounded-lg px-2 font-normal",
      "transition-colors hover:bg-popover",
      // Weight stays `font-normal` when active — the filled pill already says
      // which row you are on. Bumping it to `font-medium` as well made the row
      // read as larger type than the thread list below, which sets every title
      // at the same 14px/400 this does.
      isActive && "bg-popover",
    )}
    // `aria-current` rather than `aria-pressed`: these name where you are, and
    // the row for the view already showing is not a toggle you can un-press.
    aria-current={isActive ? "page" : undefined}
    data-statsig-name={statsigName}
    onClick={(event) => {
      onClick();
      // A pointer click leaves focus on the row, and space is a focused
      // button's own activation key — so the global new-chat shortcut yields
      // to it and reads as dead until you click elsewhere. Re-activating the
      // row you are already on does nothing, so it has nothing to gain from
      // holding space. `detail === 0` means keyboard activation; keep focus
      // there, or arrowing through the list would drop you out of it.
      if (event.detail > 0) event.currentTarget.blur();
    }}
  >
    {/* 14px — the label's own size, and between the 20px a bare header control
        gets and the 12px badges the thread rows carry. Sized to the text rather
        than to the button, so the glyph and the word it leads read as one line
        at the same scale as every line below them. */}
    <Icon className="size-3.5 shrink-0" />
    <span className="min-w-0 truncate text-sm">{label}</span>
  </Button>
);

/**
 * New chat's own row descriptor, apart from {@link NAV_ROWS} because it is the
 * one entry that is an action rather than a destination. Exported so the mobile
 * menu names it with the same word and the same glyph.
 */
export const NEW_CHAT_ROW: { value: "newchat"; label: string; icon: NavIcon } = {
  value: "newchat",
  label: PIVOT_LABELS.newchat,
  icon: SquarePen,
};

/**
 * New chat, pinned above the scrolling list.
 *
 * It sits apart because it is the one row that is an action rather than a
 * destination: everything below it says what the canvas shows, and this starts
 * something new. Staying put while the views and the threads scroll under it is
 * what makes that difference legible — and it means the way to begin is always
 * on screen, however far down the list you have gone.
 */
export const SidebarNewChatRow = ({
  isActive,
  onSelect,
}: {
  isActive: boolean;
  onSelect: (pivot: Pivot) => void;
}) => (
  <NavRow
    label={NEW_CHAT_ROW.label}
    icon={NEW_CHAT_ROW.icon}
    isActive={isActive}
    onClick={() => onSelect("newchat")}
    statsigName="sidebar-nav-new-chat"
  />
);

/**
 * The sidebar's navigation list: every canvas destination, stacked above the
 * thread list.
 *
 * Stacked rather than a segmented row: down the page each row has the full 280px
 * to itself, so every destination carries a word instead of a glyph whose
 * meaning lives in a tooltip, and the list can grow without squeezing anything.
 *
 * `activePivot` may be `null`, and that is the common case: the canvas shows a
 * thread until someone picks a view.
 */
export const SidebarNavList = ({
  activePivot,
  onSelectPivot,
}: {
  activePivot: Pivot | null;
  onSelectPivot: (pivot: Pivot) => void;
}) => {
  const navRows = useNavRows();

  return (
    // 4px between rows, matching the thread list below: these are filled pills
    // when active or hovered, and two of them touching reads as one block.
    <nav aria-label="Views" className="flex flex-col gap-1">
      {navRows.map(({ value, label, icon }) => (
        <NavRow
          key={value}
          label={label}
          icon={icon}
          isActive={activePivot === value}
          onClick={() => onSelectPivot(value)}
          statsigName={`sidebar-nav-${value}`}
        />
      ))}
    </nav>
  );
};
